import React from 'react'
import NextImage from 'next/image'
import { AnimatePresence, motion } from 'framer-motion'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { Typewriter } from 'components/Typewriter'
import { GURU_AI_AVATAR_IMAGE_PATH } from 'app/ConversationPage/constants'

const ChatSimulationAnimation = () => {
  return (
    <div className="relative w-full px-4 max-h-80 after:content-[''] after:absolute after:z-10 after:bottom-0 after:left-0 after:from-transparent after:to-white after:bg-gradient-to-b after:to-90% after:h-16 after:w-full overflow-hidden">
      <AnimatePresence>
        <motion.div
          className="border-4 border-[#0B5FEF] rounded-[40px] py-8 bg-white w-full h-full overflow-hidden relative"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            ease: 'easeInOut',
            duration: 0.6,
          }}
          viewport={{ once: true }}
        >
          <AnimatePresence>
            <motion.div
              className="flex p-4 space-x-4 bg-blue-5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                ease: 'easeInOut',
                duration: 0.4,
                delay: 3.2,
              }}
              viewport={{ once: true }}
            >
              <div className="w-6 h-6 !p-0 rounded-full bg-[#458CFF] flex-none" />
              <Text>Bagaimana langkah penyusunan modul P5?</Text>
            </motion.div>
          </AnimatePresence>
          <div className="flex p-4 space-x-4 bg-white">
            <motion.div
              className="w-6 h-6 !p-0 rounded-full bg-[#458CFF] flex-none"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                ease: 'easeInOut',
                duration: 0.4,
                delay: 3.6,
              }}
              viewport={{ once: true }}
            >
              <NextImage
                src={GURU_AI_AVATAR_IMAGE_PATH}
                className="rounded-full"
                width="24px"
                height="24px"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                ease: 'easeInOut',
                duration: 0.6,
                delay: 3.9,
              }}
              viewport={{ once: true }}
            >
              <Text>
                <Typewriter
                  delay={3.8}
                  speed={0.008}
                  text="
                Siap. Langkah penyusunan modul P5 (Projek Penguatan Profil
                Pelajar Pancasila) melibatkan beberapa tahapan yang dapat
                disesuaikan dengan kondisi dan kebutuhan peserta didik. Berikut
                adalah langkah-langkahnya: Identifikasi dan Pemetaan Kondisi
                serta Kebutuhan Peserta Didik: Langkah pertama adalah
                mengidentifikasi kondisi dan kebutuhan peserta didik. Ini
                termasuk memahami latar belakang, minat, dan kebutuhan belajar
                mereka. Penentuan Perancangan Modul Berdasarkan Tahap Kesiapan
                Satuan Pendidikan: Setelah mengidentifikasi kebutuhan peserta
                didik, langkah berikutnya adalah menentukan perancangan modul
                berdasarkan kesiapan satuan pendidikan. Ini mencakup penilaian
                terhadap sumber daya yang tersedia dan kesiapan guru serta
                fasilitas sekolah."
                />
              </Text>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>
      <AnimatePresence>
        <motion.div
          className="absolute bottom-0 left-0 right-0 flex flex-row items-center justify-between w-full max-w-xl px-8 py-4 space-x-3"
          initial={{ opacity: 0 }}
          animate={{ y: [50, -30, -30, 100], opacity: [0, 100, 100, 100] }}
          transition={{ ease: 'easeOut', duration: 3.5, delay: 1 }}
        >
          <div className="w-full p-3 border-2 rounded-sm border-gray-30">
            <Text>
              <Typewriter
                text="Bagaimana langkah penyusunan modul P5?"
                delay={1.8}
                speed={0.04}
              />
            </Text>
          </div>
          <div className="flex-none">
            <Button
              color="black"
              disabled
              iconOnly
              size="sm"
              className="!rounded-full !overflow-auto"
            >
              <Icon color="inverse" fontSize="sm">
                send
              </Icon>
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

interface IConversationLandingOnboardingPartTwoProps {
  onClick: () => void
}
const ConversationLandingOnboardingPartTwo: React.FC<
  IConversationLandingOnboardingPartTwoProps
> = ({ onClick }) => {
  return (
    <>
      <motion.div
        className="flex items-center justify-center max-w-xl mx-auto mb-8 space-x-0 bg-[url('/images/asisten-guru/bg-pattern.svg')] bg-no-repeat bg-center bg-cover"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{
          ease: 'easeInOut',
          duration: 0.8,
        }}
        viewport={{ once: true }}
      >
        <ChatSimulationAnimation />
      </motion.div>

      <AnimatePresence>
        <motion.div
          className="flex items-center justify-center mx-auto space-x-0 max-w-7xl lg:flex-row lg:space-x-10 2xl:space-x-20"
          initial={{ opacity: 0, y: '10%' }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ ease: 'easeIn', duration: 0.2, delay: 4.8 }}
          viewport={{ once: true }}
        >
          <Text variant="display-md">Silakan tanya apa saja.</Text>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        <motion.div
          className="flex items-center justify-center mx-auto space-x-0 max-w-7xl lg:flex-row lg:space-x-10 2xl:space-x-20"
          initial={{ opacity: 0, y: '10%' }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ ease: 'easeIn', duration: 0.2, delay: 4.9 }}
          viewport={{ once: true }}
        >
          <Text variant="display-md">Aru bisa langsung</Text>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        <motion.div
          className="flex items-center justify-center mx-auto space-x-0 max-w-7xl lg:flex-row lg:space-x-10 2xl:space-x-20"
          initial={{ opacity: 0, y: '10%' }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ ease: 'easeIn', duration: 0.2, delay: 5 }}
          viewport={{ once: true }}
        >
          <Text variant="display-md">bantu.</Text>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        <motion.div
          className="max-w-xl mx-auto fixed bottom-0 left-0 right-0 p-4 space-y-4 bg-white shadow-[inset_0px_5px_3px_-3px_rgba(0,0,0,0.08)]"
          initial={{ opacity: 0, y: '10%' }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ ease: 'easeInOut', duration: 0.6, delay: 5.6 }}
          viewport={{ once: true }}
        >
          <div className="flex items-end gap-x-3">
            <Button color="blue" size="lg" fullWidth onClick={onClick}>
              Tanya sekarang
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  )
}

export default ConversationLandingOnboardingPartTwo
